'use strict'

/**
 * Merge the webchatSlots received from the client with the existing slots in the state
 * If a slot already exists, it will be overwritten by the new value
 * @param {Object} existingSlots - Existing slots in the state [{slug: value}]
 * @param {Object} slotsToAdd - Slots received from the client {slug: value}
 * @returns {Array} Merged slots
 */
export const mergeWithStateWebchatSlots = (existingSlots, slotsToAdd) => {
  const existingSlotsMap = existingSlots.reduce((acc, obj) => ({ ...acc, ...obj }), {})
  const mergedSlots = { ...existingSlotsMap, ...slotsToAdd }
  return Object.entries(mergedSlots).map(([key, value]) => ({ [key]: value }))
}

/**
 * Extract the slots values from the postMessage event data based on the postMessageSlotsProperties
 * @param {Array} postMessageSlotsProperties - Array of slot properties {slug, fillFromPostMessageObjectKey: [{locale, objectKey}]}
 * @param {Object} eventData - Data received from the postMessage event
 * @param {String} locale - Current locale of the webchat
 * @returns {Object} Extracted slots {slug: value}
 */
export const extractClientPostMessageData = (postMessageSlotsProperties, eventData, locale) => {
  // client can fill in multiple slots from a single postMessage
  const postMessageSlots = {}

  postMessageSlotsProperties.forEach(slot => {
    const fillFromPostMessageObject = slot.fillFromPostMessageObjectKey.find(obj => obj.locale === locale)
    if (!fillFromPostMessageObject || !fillFromPostMessageObject.objectKey) {
      return
    }

    // The event is not matching our slot
    if (!(fillFromPostMessageObject.objectKey in eventData)) {
      return
    }
    postMessageSlots[slot.slug] = eventData[fillFromPostMessageObject.objectKey]
  })

  return postMessageSlots
}

/**
 * Trim white spaces, replace whitespaces and dashes with underscores, convert to lower case, and remove diacritics (accents)
 * @param {String} string - the string to manipulate
 * @return {String} the transformed string
 */
export const normalizeLowerCase = string => {
  string = ('' + string).trim() // Security cast + remove start and end whitespaces
  string = string.normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove the diacritics from string
  string = string.replace(/[\s-]+/g, '_') // replace other whitespaces and dashes by _
  return string.toLowerCase() // force lowercase
}

/**
 * Check if the webchat slot value is matching the trigger slot requirements
 * @param {String} slotValue - The webchat slot value
 * @param {Array} possibleSlotValues - The possible slot values defined in the trigger slot
 * @param {Array} excludedSlotValues - The excluded slot values defined in the trigger slot
 * @param {Boolean} canSlotValueBeEmpty - If true, the slot value can be empty
 * @param {Boolean} canSlotValueBeAnything - If true, the slot value can be anything
 * @returns {Boolean} True if the webchat slot value is matching the trigger slot requirements
 */
const isWebchatSlotValueMatchingRequirements = (
  slotValue,
  possibleSlotValues,
  excludedSlotValues,
  canSlotValueBeEmpty,
  canSlotValueBeAnything
) => {
  const normalizedSlotValue = slotValue === null || slotValue === undefined ? '' : normalizeLowerCase(slotValue)

  if ((canSlotValueBeEmpty && !normalizedSlotValue) || (canSlotValueBeAnything && normalizedSlotValue)) {
    return true
  }

  const isPossibleSlotValueRequirementMet =
    !possibleSlotValues.length || possibleSlotValues.includes(normalizedSlotValue)
  const isExcludedSlotValueRequirementMet =
    !excludedSlotValues.length || !excludedSlotValues.includes(normalizedSlotValue)

  return isPossibleSlotValueRequirementMet && isExcludedSlotValueRequirementMet
}

/**
 * Check if the trigger slot is matching the webchat slots
 * @param {Object} triggerSlot - The trigger slot
 * @param {Object} webchatSlots - The webchat slots {slug: value}
 * @returns {Boolean} True if the trigger slot is matching the webchat slots
 */
export const isTriggerSlotValueMatchingWebchatSlot = (triggerSlot, webchatSlots) => {
  // Slot not set properly, let's consider it not matching since it cannot be evaluated
  if (!triggerSlot || !triggerSlot.slotId || !triggerSlot.slotId.slug) {
    return false
  }

  const normalizedPossibleSlotValues =
    (triggerSlot.possibleSlotValues && triggerSlot.possibleSlotValues.map(value => normalizeLowerCase(value))) || []

  const normalizedExcludedSlotValues =
    (triggerSlot.excludedSlotValues && triggerSlot.excludedSlotValues.map(value => normalizeLowerCase(value))) || []

  if (triggerSlot.slotId.slug in webchatSlots) {
    return isWebchatSlotValueMatchingRequirements(
      webchatSlots[triggerSlot.slotId.slug],
      normalizedPossibleSlotValues,
      normalizedExcludedSlotValues,
      triggerSlot.canSlotValueBeEmpty,
      triggerSlot.canSlotValueBeAnything
    )
  }

  // If the slot is not found in the webchat slots, we consider its value as empty
  return triggerSlot.canSlotValueBeEmpty
}

export default {
  extractClientPostMessageData,
  mergeWithStateWebchatSlots,
  isTriggerSlotValueMatchingWebchatSlot
}
